import React, { useState } from "react";
import {
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Alert from "../components/Alert";
import SecurityNotice from "../components/SecurityNotice";
import ActionButton from "../components/ActionButton";
import CopyValueButton from "../components/CopyValueButton";
import SEO from "../components/SEO";
import { useScrollToResults } from "../hooks/useScrollToResults";
import { useCopyFeedback } from "../hooks/useCopyFeedback";
import api from "../utils/api";
import { DCV_METHODS } from "../../shared/constants/dcvMethods";
// 2026-06-01 : real Top-Level Domain (TLD) validation via tldts,
// shared with the server. See src/shared/utils/domainValidation.js.
import { isRegistrablePublicDomain } from "../../shared/utils/domainValidation";

/**
 * Help text for the Validation Details section, keyed by method type
 */
const DNS_HELP_TEXT =
  "Enter the DNS record details exactly as provided by your CA. For example, your CA may provide a record like _07EE48242DC304C77B9695167579BB61.example.com pointing to BCF85C0B38933F4ED9BC28DF135B7E72.sectigo.com - enter the prefix portion below (without your domain) and the expected target value. CNAME and TXT record matching is case-insensitive.";

const HTTP_HELP_TEXT =
  "Enter the file details provided by your CA for HTTP-based validation. The file must be accessible at http(s)://yourdomain/.well-known/pki-validation/ with the exact content specified. File content matching is case-sensitive - ensure the content matches exactly as provided by your CA.";

/**
 * DCV Checker Page
 * Checks CNAME, TXT, HTTP, and HTTPS validation records for SSL Certificate issuance
 */
const DCVChecker = () => {
  // State management
  const [domain, setDomain] = useState("");
  const [method, setMethod] = useState(DCV_METHODS.CNAME);
  const [expectedValue, setExpectedValue] = useState("");
  const [recordName, setRecordName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  // Transient per-field copy feedback : the shared success-gated
  // "Copied!" hook (see src/client/hooks/useCopyFeedback.js). The
  // 2026-08-07 download/copy consistency pass (operator-directed)
  // added the copy buttons themselves : this page displayed the
  // record name, expected value, and found value - exactly the
  // strings a customer transcribes into a DNS control panel or a
  // support ticket - with no copy affordance at all.
  const { copyStatus, handleCopy, resetCopyStatus } = useCopyFeedback();
  const resultsRef = useScrollToResults(result);

  /**
   * Validate form inputs
   */
  const validateForm = () => {
    const newErrors = {};

    if (!domain.trim()) {
      newErrors.domain = "A domain name is required.";
    } else if (!isRegistrablePublicDomain(domain.trim().replace(/^\*\./, ""))) {
      // Real Top-Level Domain (TLD) check for instant feedback ; the
      // server applies the same gate authoritatively.
      newErrors.domain =
        "The domain name uses an unrecognized or non-public Top-Level Domain (TLD).";
    }

    if (!expectedValue.trim()) {
      newErrors.expectedValue = "An expected value is required.";
    }

    if (
      (method === DCV_METHODS.HTTP || method === DCV_METHODS.HTTPS) &&
      !recordName.trim()
    ) {
      newErrors.recordName =
        "A file name is required for HTTP or HTTPS validation.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    resetCopyStatus();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const data = {
        domain: domain.trim(),
        method,
        expectedValue: expectedValue.trim(),
      };

      // Include recordName for all methods - defaults handled server-side for DNS
      if (recordName.trim()) {
        data.recordName = recordName.trim();
      }

      const response = await api.checkDCV(data);
      setResult(response.data);
    } catch (err) {
      setError(err.message || "The domain validation could not be checked.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle reset button
   */
  const handleReset = () => {
    setDomain("");
    setMethod(DCV_METHODS.CNAME);
    setExpectedValue("");
    setRecordName("");
    setError(null);
    setErrors({});
    setResult(null);
    resetCopyStatus();
  };

  const isDnsMethod =
    method === DCV_METHODS.CNAME || method === DCV_METHODS.TXT;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 2026-07-04 tools-wide overhaul (operator-approved) : canonicalUrl
          added so search engines index the tool at its one true address. */}
      <SEO
        title="DCV Checker - Verify Domain Control Validation Records for SSL Certificates"
        description="Verify domain control validation records for SSL Certificate issuance. Check CNAME, TXT, HTTP, and HTTPS validation methods to confirm correct DNS record or file configuration for Domain Validated (DV) SSL Certificates."
        keywords="DCV checker, domain control validation, DV SSL Certificate, CNAME validation, TXT validation, HTTP file validation, pki-validation, SSL Certificate domain verification, domain control validation checker"
        canonicalUrl="https://tools.trustico.com/dcv-checker"
      />

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-heading mb-4 text-center">
          DCV Checker
        </h1>
        <p className="text-body">
          Domain Control Validation (DCV) is the process of proving ownership or
          control of a domain before an SSL Certificate can be issued. Your
          Certificate Authority (CA) will provide the specific values to use for
          validation. Use this tool to verify that your records or files are
          correctly configured.
        </p>
      </div>

      <SecurityNotice />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-heading mb-2">
            Validation Method
          </h2>
          <p className="text-body mb-4">
            Select the domain control validation method specified by your CA.
            The most common method is CNAME, but your CA may require a different
            method depending on your SSL Certificate type.
          </p>
          <div className="space-y-4">
            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                name="method"
                value={DCV_METHODS.CNAME}
                checked={method === DCV_METHODS.CNAME}
                onChange={(e) => {
                  setMethod(e.target.value);
                  setRecordName("");
                  setErrors({});
                }}
                className="h-4 w-4 text-link focus:ring-blue-500 border-control mt-1"
              />
              <div className="ml-3">
                <span className="font-medium text-heading">CNAME Record</span>
                <p className="text-body mt-2">
                  Verify a CNAME DNS record set for domain control validation.
                  This is the most common method used by CAs. Your CA will
                  provide a unique CNAME record to add to your DNS, typically
                  using a subdomain prefix like _dnsauth.
                </p>
              </div>
            </label>
            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                name="method"
                value={DCV_METHODS.TXT}
                checked={method === DCV_METHODS.TXT}
                onChange={(e) => {
                  setMethod(e.target.value);
                  setRecordName("");
                  setErrors({});
                }}
                className="h-4 w-4 text-link focus:ring-blue-500 border-control mt-1"
              />
              <div className="ml-3">
                <span className="font-medium text-heading">TXT Record</span>
                <p className="text-body mt-2">
                  Verify a TXT DNS record set for domain control validation.
                  Some CAs use TXT records as an alternative to CNAME. The TXT
                  record is typically set at a specific subdomain prefix
                  provided by your CA.
                </p>
              </div>
            </label>
            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                name="method"
                value={DCV_METHODS.HTTP}
                checked={method === DCV_METHODS.HTTP}
                onChange={(e) => {
                  setMethod(e.target.value);
                  setRecordName("");
                  setErrors({});
                }}
                className="h-4 w-4 text-link focus:ring-blue-500 border-control mt-1"
              />
              <div className="ml-3">
                <span className="font-medium text-heading">HTTP File</span>
                <p className="text-body mt-2">
                  Verify a file placed at
                  http://domain/.well-known/pki-validation/ for domain control
                  validation over HTTP. Your CA will provide the file name and
                  content to place on your web server.
                </p>
              </div>
            </label>
            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                name="method"
                value={DCV_METHODS.HTTPS}
                checked={method === DCV_METHODS.HTTPS}
                onChange={(e) => {
                  setMethod(e.target.value);
                  setRecordName("");
                  setErrors({});
                }}
                className="h-4 w-4 text-link focus:ring-blue-500 border-control mt-1"
              />
              <div className="ml-3">
                <span className="font-medium text-heading">HTTPS File</span>
                <p className="text-body mt-2">
                  Verify a file placed at
                  https://domain/.well-known/pki-validation/ for domain control
                  validation over HTTPS. This is the same as HTTP validation but
                  requires a valid SSL Certificate to already be installed on
                  the server.
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="card p-6">
          <h2 className="text-lg font-semibold text-heading mb-2">
            Domain Name
          </h2>
          <p className="text-body mb-4">
            Enter the domain name being validated for your SSL Certificate. Do
            not include http:// or https:// protocols. Enter only the domain
            name such as example.com or subdomain.example.com.
          </p>
          <input
            type="text"
            value={domain}
            onChange={(e) => {
              setDomain(e.target.value);
              if (errors.domain) setErrors({ ...errors, domain: null });
            }}
            placeholder="example.com"
            className="input"
          />
          {errors.domain && <p className="error-text">{errors.domain}</p>}
        </div>

        <div className="card p-6">
          <h2 className="text-lg font-semibold text-heading mb-2">
            Validation Details
          </h2>
          <p className="text-body mb-4">
            {isDnsMethod ? DNS_HELP_TEXT : HTTP_HELP_TEXT}
          </p>

          <div className="space-y-4">
            <div>
              {/* 2026-07-04 operator consistency directive : the
                  optional marker is "(Optional)" to match the sibling
                  labels "PFX Password (Optional)" (PFXConverter.jsx)
                  and "TLS Certificate Hashes (Optional, SHA-256)"
                  (DNSStampGenerator.jsx) ; the previous " - Optional"
                  suffix was the only hyphen-style marker in the suite. */}
              <label className="label">
                {isDnsMethod ? "Record Name (Optional)" : "File Name *"}
              </label>
              <input
                type="text"
                value={recordName}
                onChange={(e) => {
                  setRecordName(e.target.value);
                  if (errors.recordName)
                    setErrors({ ...errors, recordName: null });
                }}
                placeholder={
                  isDnsMethod
                    ? "_07EE48242DC304C77B9695167579BB61"
                    : "BCF85C0B38933F4ED9BC28DF135B7E72.txt"
                }
                className="input"
              />
              {errors.recordName && (
                <p className="error-text">{errors.recordName}</p>
              )}
            </div>

            <div>
              <label className="label">
                {isDnsMethod
                  ? `Expected ${method.toUpperCase()} Value *`
                  : "Expected File Content *"}
              </label>
              {isDnsMethod ? (
                <input
                  type="text"
                  value={expectedValue}
                  onChange={(e) => {
                    setExpectedValue(e.target.value);
                    if (errors.expectedValue)
                      setErrors({ ...errors, expectedValue: null });
                  }}
                  placeholder={
                    method === DCV_METHODS.CNAME
                      ? "BCF85C0B38933F4ED9BC28DF135B7E72.2C4857477ACA4A470F57F3EC3B0ABFB7.sectigo.com"
                      : "CA3A27F1B6E248D6966C8C17A4AF9A4C07C605B4"
                  }
                  maxLength={2048}
                  className="input"
                />
              ) : (
                /* 2026-07-04 operator consistency directive :
                   placeholders are realistic example values, never
                   instructional prose (was "Paste the expected file
                   content here..."). The sample mirrors the two-line
                   shape of a Sectigo® HTTP validation file : the
                   request hash on the first line and the CA domain on
                   the second. */
                <textarea
                  value={expectedValue}
                  onChange={(e) => {
                    setExpectedValue(e.target.value);
                    if (errors.expectedValue)
                      setErrors({ ...errors, expectedValue: null });
                  }}
                  placeholder="CA3A27F1B6E248D6966C8C17A4AF9A4C07C605B4&#10;comodoca.com"
                  rows={4}
                  maxLength={2048}
                  className="input font-mono"
                />
              )}
              {errors.expectedValue && (
                <p className="error-text">{errors.expectedValue}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
            {/* 2026-07-04 operator consistency directive : the primary
                action is disabled until every required input for the
                selected method is non-empty (domain and expected value
                always ; the file name only for HTTP/HTTPS, where it is
                required). validateForm keeps its required-field checks
                as the direct-form-submission backstop and still
                supplies the on-click TLD format validation. */}
            <ActionButton
              type="submit"
              variant="primary"
              disabled={
                loading ||
                !domain.trim() ||
                !expectedValue.trim() ||
                ((method === DCV_METHODS.HTTP ||
                  method === DCV_METHODS.HTTPS) &&
                  !recordName.trim())
              }
              loading={loading}
              loadingText="Checking..."
              icon={ClipboardDocumentCheckIcon}
            >
              Check Validation
            </ActionButton>
            <ActionButton
              type="button"
              onClick={handleReset}
              variant="outline"
              icon={XMarkIcon}
            >
              {/* 2026-07-04 operator consistency directive : reset
                  buttons across every tool page say "Reset" (the
                  previous label was "Clear Data"). */}
              Reset
            </ActionButton>
          </div>
        </div>
      </form>

      {/* 2026-07-04 tools-wide overhaul (operator-approved), "Loading
          idiom" row of the divergence map in
          docs/TOOLS_CONSISTENCY_AUDIT.md : the separate LoadingSpinner
          card was removed - the submit ActionButton's inline
          loading/loadingText="Checking..." state is the standard loading
          treatment across the tools. */}

      {error && (
        <Alert type="error" className="mt-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* `!loading` guard dropped with the spinner card above : result is
          cleared at submit start (handleSubmit), so result and loading can
          never be truthy together. */}
      {result && (
        <div ref={resultsRef} className="mt-8 animate-fade-in">
          <div className="card p-6">
            {/* 2026-07-04 operator consistency directive : recased
                from h2 text-2xl font-bold to the reference section
                heading size (text-lg font-semibold), and given the
                explanatory paragraph every section heading carries. */}
            <h2 className="text-lg font-semibold text-heading mb-2">
              Validation Results
            </h2>
            <p className="text-body mb-4">
              This verdict shows whether your domain control validation record
              or file matches the value your Certificate Authority (CA) expects.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                <span className="text-xs font-medium text-muted uppercase tracking-wider">
                  Status
                </span>
                <div className="flex items-center mt-1">
                  {result.isValid ? (
                    <>
                      <CheckCircleIcon className="h-5 w-5 text-green-700 dark:text-green-400 mr-2" />
                      <span className="font-medium text-green-700 dark:text-green-400">
                        VALID
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-300 mr-2" />
                      <span className="font-medium text-red-600 dark:text-red-300">
                        INVALID
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                <span className="text-xs font-medium text-muted uppercase tracking-wider">
                  Method
                </span>
                <p className="text-heading mt-1">
                  {result.method.toUpperCase()}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                <span className="text-xs font-medium text-muted uppercase tracking-wider">
                  Checked At
                </span>
                <p className="text-heading mt-1">
                  {new Date(result.checkedAt).toLocaleString()}
                </p>
              </div>
            </div>

            <Alert
              type={result.isValid ? "success" : "warning"}
              className="mb-4"
            >
              {result.details}
            </Alert>
          </div>

          <div className="card p-6 mt-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Check Details
            </h2>
            {/* Explanatory paragraph per the 2026-07-04 operator
                consistency directive (see the Validation Results card
                above). */}
            <p className="text-body mb-4">
              These details show exactly what was checked and what was found, so
              you can correct any mismatch.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 2026-08-07 download/copy consistency pass
                  (operator-directed) : the four value fields below
                  each carry a compact copy button (see the
                  CopyValueButton JSDoc above). The Method / Checked At
                  / Lookup Method fields deliberately do not : they are
                  status descriptions, not values a customer
                  transcribes anywhere. Only Found Value gates its
                  button on the value being present : domain,
                  recordName, and expectedValue are guaranteed
                  non-empty by the route's validation (the check
                  cannot run without them), while foundValue is
                  legitimately absent when nothing was found and its
                  placeholder text must not be copyable. */}
              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Domain
                  </span>
                  <CopyValueButton
                    label="Domain"
                    value={result.domain}
                    fieldKey="domain"
                    copyStatus={copyStatus}
                    onCopy={handleCopy}
                  />
                </div>
                <p className="font-mono text-heading mt-1">{result.domain}</p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    {isDnsMethod ? "Record Checked" : "URL Checked"}
                  </span>
                  <CopyValueButton
                    label={isDnsMethod ? "Record Checked" : "URL Checked"}
                    value={result.recordName}
                    fieldKey="recordName"
                    copyStatus={copyStatus}
                    onCopy={handleCopy}
                  />
                </div>
                <p className="font-mono text-heading mt-1 break-all">
                  {result.recordName}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Expected Value
                  </span>
                  <CopyValueButton
                    label="Expected Value"
                    value={result.expectedValue}
                    fieldKey="expectedValue"
                    copyStatus={copyStatus}
                    onCopy={handleCopy}
                  />
                </div>
                <p className="font-mono text-heading mt-1 break-all">
                  {result.expectedValue}
                </p>
              </div>

              <div
                className={`p-4 rounded-lg ${
                  result.isValid
                    ? "bg-green-50 dark:bg-green-900/30"
                    : result.foundValue
                      ? "bg-red-50 dark:bg-red-900/30"
                      : "bg-yellow-50 dark:bg-yellow-900/30"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Found Value
                  </span>
                  {/* The copy button only renders when a value was
                      actually found AND is complete : the "No Record
                      Found" / "Not Retrieved" placeholder is
                      explanatory text, and a truncated HTTP/HTTPS
                      value (dcvService caps long responses and
                      appends "... (truncated)") is not transcribable
                      either - copying it would put a silently partial
                      value on the clipboard while the truncation note
                      below stays behind on screen. */}
                  {result.foundValue && !result.foundValueTruncated && (
                    <CopyValueButton
                      label="Found Value"
                      value={result.foundValue}
                      fieldKey="foundValue"
                      copyStatus={copyStatus}
                      onCopy={handleCopy}
                    />
                  )}
                </div>
                <p
                  className={`font-mono mt-1 break-all ${
                    result.isValid
                      ? "text-green-700 dark:text-green-300"
                      : result.foundValue
                        ? "text-red-700 dark:text-red-300"
                        : "text-yellow-700 dark:text-yellow-300"
                  }`}
                >
                  {result.foundValue ||
                    (result.method === DCV_METHODS.HTTP ||
                    result.method === DCV_METHODS.HTTPS
                      ? "Not Retrieved"
                      : "No Record Found")}
                </p>
                {result.foundValueTruncated && (
                  <p className="text-muted mt-1">
                    Response content was truncated for display. The full content
                    was used for validation comparison.
                  </p>
                )}
                {result.foundValues && result.foundValues.length > 1 && (
                  <div className="mt-2">
                    <p className="text-muted mb-1">
                      <strong>
                        All {result.foundValues.length}{" "}
                        {result.method === DCV_METHODS.CNAME ? "CNAME" : "TXT"}{" "}
                        records found :
                      </strong>
                    </p>
                    <ul className="font-mono text-body space-y-1">
                      {result.foundValues.map((v, i) => (
                        <li key={`${i}-${v}`} className="break-all">
                          {v}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                <span className="text-xs font-medium text-muted uppercase tracking-wider">
                  Lookup Method
                </span>
                <p className="text-heading mt-1">
                  {result.dnsMethod === "DoH"
                    ? "DNS-over-HTTPS (Google Public DNS)"
                    : result.dnsMethod === "native"
                      ? "Native DNS Resolution"
                      : `Direct ${result.method.toUpperCase()} File Retrieval`}
                </p>
              </div>
            </div>
          </div>

          {(result.method === DCV_METHODS.CNAME ||
            result.method === DCV_METHODS.TXT) && (
            <Alert type="info" className="mt-6">
              <strong>DNS Cache Notice :</strong> DNS results may be cached by
              Google Public DNS. If you recently updated your records, allow
              time for DNS propagation before checking again. TTL values set on
              your DNS records determine how long cached results persist.
            </Alert>
          )}
        </div>
      )}
    </div>
  );
};

export default DCVChecker;
